/**
 * SourceVerificationView — the 4th comparison view mode (F6-6).
 *
 * Renders the legal source-verification results as a self-contained list,
 * driven entirely by GET /comparisons/{id}/source-verification. lawyer/reviewer
 * can trigger a run from the toolbar; viewer is read-only. Verdicts sort by
 * actionability (fail → warning → unverifiable → pass).
 *
 * Verdict node ids come from the materialized working tree, which the diff
 * views don't share, so this view does not anchor badges into the document DOM
 * — each card carries its own citation + drill-down context.
 */
import { useMemo } from 'react';

import type { VeriDocRole, VerificationResult } from '../../lib/veridoc-api';

import { OVERALL_SEVERITY } from './constants';
import { SourceVerificationEmpty } from './SourceVerificationEmpty';
import { useSourceVerification } from './useSourceVerification';
import { VerdictCard } from './VerdictCard';
import { VerificationToolbar } from './VerificationToolbar';

interface SourceVerificationViewProps {
  comparisonId: string;
  userRole: VeriDocRole | null;
  active: boolean;
  onError: (message: string) => void;
}

function sortByActionability(
  results: VerificationResult[],
): VerificationResult[] {
  return [...results].sort(
    (a, b) =>
      OVERALL_SEVERITY[a.payload.verdict.overall] -
      OVERALL_SEVERITY[b.payload.verdict.overall],
  );
}

export function SourceVerificationView({
  comparisonId,
  userRole,
  active,
  onError,
}: SourceVerificationViewProps): React.JSX.Element {
  const canRun = userRole === 'lawyer' || userRole === 'reviewer';
  const verification = useSourceVerification({
    comparisonId,
    enabled: active,
    canRun,
    onError,
  });

  const sorted = useMemo(
    () => sortByActionability(verification.results),
    [verification.results],
  );

  const noCitationsFound =
    verification.ranThisSession &&
    verification.lastRun != null &&
    verification.lastRun.processedCount === 0 &&
    verification.lastRun.failedCount === 0;

  return (
    <div className="flex flex-col gap-4">
      <VerificationToolbar
        results={verification.results}
        lastRun={verification.lastRun}
        running={verification.running}
        canRun={canRun}
        onRun={() => void verification.run()}
      />

      {verification.lastRun && verification.lastRun.failedCount > 0 && (
        <div className="rounded-md border border-semantic-warning bg-semantic-warningBg px-4 py-3">
          <p className="text-sm font-semibold text-semantic-warning">
            {verification.lastRun.failedCount} 条引用因来源库或 LLM 不可达而降级
          </p>
          <p className="mt-1 text-xs text-ink-700">
            这些引用标记为无法核验或内容一致性未评，已成功的结论不受影响。配置可用的
            LLM 服务后重新核验即可补全。
          </p>
        </div>
      )}

      <Body
        status={verification.status}
        results={sorted}
        running={verification.running}
        canRun={canRun}
        noCitationsFound={noCitationsFound}
        onRetry={() => void verification.refresh()}
      />
    </div>
  );
}

function Body({
  status,
  results,
  running,
  canRun,
  noCitationsFound,
  onRetry,
}: {
  status: ReturnType<typeof useSourceVerification>['status'];
  results: VerificationResult[];
  running: boolean;
  canRun: boolean;
  noCitationsFound: boolean;
  onRetry: () => void;
}): React.JSX.Element {
  if (status === 'loading' || status === 'idle') {
    return <Centered text="加载核验结果…" />;
  }
  if (status === 'error') {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border border-ink-200 bg-ink-0 px-6 py-12 text-center shadow-sm">
        <p className="text-sm text-ink-500">加载核验结果失败。</p>
        <button
          type="button"
          onClick={onRetry}
          className="rounded-sm border border-ink-200 bg-ink-50 px-3 py-1.5 text-xs text-ink-600 hover:bg-ink-100 hover:text-ink-900"
        >
          重试
        </button>
      </div>
    );
  }
  if (results.length === 0) {
    return (
      <SourceVerificationEmpty
        canRun={canRun}
        running={running}
        noCitationsFound={noCitationsFound}
      />
    );
  }
  return (
    <ul className="flex flex-col gap-3">
      {results.map((result) => (
        <VerdictCard key={result.id} result={result} />
      ))}
    </ul>
  );
}

function Centered({ text }: { text: string }): React.JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[40vh] items-center justify-center text-sm text-ink-500"
    >
      {text}
    </div>
  );
}

export default SourceVerificationView;
